import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiX } from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { FaFacebook } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { getAuthErrorMessage, isDismissedAuthError } from '../utils/authErrors';

const inputClasses =
  'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all';

const AuthModals = ({ isOpen, onClose, type }) => {
  const [mode, setMode] = useState(type); // 'login' | 'signup' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null); // 'google' | 'facebook' | null
  const { signup, login, loginWithGoogle, loginWithFacebook, resetPassword } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setMode(type);
      setError('');
      setResetSent(false);
      setPassword('');
    }
  }, [isOpen, type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mode === 'reset') {
      setSubmitting(true);
      try {
        await resetPassword(email);
        setResetSent(true);
      } catch (err) {
        setError(getAuthErrorMessage(err));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'signup') {
        await signup(email, password);
      } else {
        await login(email, password);
      }
      onClose();
      navigate('/dashboard');
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setError('');
    setSocialLoading(provider);
    try {
      await (provider === 'google' ? loginWithGoogle() : loginWithFacebook());
      onClose();
      navigate('/dashboard');
    } catch (err) {
      if (!isDismissedAuthError(err)) {
        setError(getAuthErrorMessage(err));
      }
    } finally {
      setSocialLoading(null);
    }
  };

  if (!isOpen) return null;

  const titles = { login: 'Welcome back', signup: 'Create your account', reset: 'Reset Password' };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="relative bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-full p-1"
        >
          <FiX className="w-5 h-5" />
        </button>

        <h2 className="text-2xl font-bold text-gray-900 mb-1">{titles[mode]}</h2>
        {mode !== 'reset' && (
          <p className="text-gray-500 text-sm mb-6">
            {mode === 'signup' ? 'Sign up to start creating with Eunbro.' : 'Log in to manage your animations and orders.'}
          </p>
        )}

        {mode === 'reset' ? (
          resetSent ? (
            <div className="mt-6">
              <p className="text-gray-700 mb-6">
                If an account exists for <span className="font-medium">{email}</span>, a password
                reset link is on its way. Check your inbox (and spam folder).
              </p>
              <button
                type="button"
                onClick={() => setMode('login')}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
              >
                Back to Log In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-4">
              <p className="text-gray-600 mb-4 text-sm">
                Enter your email and we'll send you a link to reset your password.
              </p>
              <div className="mb-6">
                <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="reset-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClasses}
                  placeholder="Enter your email"
                  required
                />
              </div>
              {error && (
                <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4">
                  {error}
                </p>
              )}
              <div className="flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="text-sm text-gray-600 hover:text-gray-800 flex-shrink-0"
                >
                  &larr; Back to Log In
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending...' : 'Send Reset Link'}
                </button>
              </div>
            </form>
          )
        ) : (
          <>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClasses}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  {mode === 'login' && (
                    <button
                      type="button"
                      onClick={() => {
                        setError('');
                        setMode('reset');
                      }}
                      className="text-sm text-indigo-600 hover:text-indigo-700"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClasses}
                  placeholder="Enter your password"
                  required
                />
              </div>

              {error && (
                <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mt-4">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full mt-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Please wait...
                  </>
                ) : mode === 'signup' ? (
                  'Sign Up'
                ) : (
                  'Log In'
                )}
              </button>
            </form>

            <p className="text-center text-sm text-gray-600 mt-4">
              {mode === 'signup' ? (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setError('');
                      setMode('login');
                    }}
                    className="text-indigo-600 font-medium hover:text-indigo-700"
                  >
                    Log in
                  </button>
                </>
              ) : (
                <>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setError('');
                      setMode('signup');
                    }}
                    className="text-indigo-600 font-medium hover:text-indigo-700"
                  >
                    Sign up
                  </button>
                </>
              )}
            </p>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-gray-400 uppercase tracking-wide">or continue with</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleSocialLogin('google')}
                disabled={socialLoading !== null}
                className="flex items-center justify-center gap-2 py-2.5 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <FcGoogle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700">
                  {socialLoading === 'google' ? 'Connecting...' : 'Google'}
                </span>
              </button>
              <button
                type="button"
                onClick={() => handleSocialLogin('facebook')}
                disabled={socialLoading !== null}
                className="flex items-center justify-center gap-2 py-2.5 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <FaFacebook className="w-5 h-5 flex-shrink-0 text-[#1877F2]" />
                <span className="text-sm font-medium text-gray-700">
                  {socialLoading === 'facebook' ? 'Connecting...' : 'Facebook'}
                </span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModals;
